import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import { Box, Fade, IconButton, Stack, styled, Tooltip, Typography, Avatar } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTH_TESTIMONIALS } from './auth-testimonials';
import { useLateNightEasterEgg } from '@/hooks/use-late-night-easter-egg';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';
import { LANGUAGE_COOKIE } from '@/libs/i18n/settings';
import Cookies from 'js-cookie';

// ─── Layout Shell ────────────────────────────────────────────────────────────

const AuthShell = styled(Box)(({ theme }) => ({
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
    flex: '1 1 75%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--mui-palette-background-paper-elevation-2)',
    color: 'var(--mui-palette-text-primary)',
    padding: theme.spacing(4),
    position: 'relative',
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
        padding: theme.spacing(4, 2, 6),
    },
}));

// Right panel is always deep blue — a brand accent, not a background surface.
// White text + logos remain readable in both light and dark mode.
const RightPanel = styled(Box)(({ theme }) => ({
    flex: '1 1 25%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--mui-palette-primary-main)',
    color: 'var(--mui-palette-primary-contrastText)',
    padding: theme.spacing(6, 5),
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
        padding: theme.spacing(4, 3),
    },
    ...theme.applyStyles('dark', {
        backgroundColor: 'var(--mui-palette-primary-dark)',
    }),
}));

// ─── Trusted Brands ──────────────────────────────────────────────────────────

const TRUSTED_BRANDS = [
    { name: 'Renascença', src: '/brands/renascenca.svg' },
    { name: 'RE/MAX', src: '/brands/remax.svg' },
    { name: 'RTP', src: '/brands/rtp.svg' },
    { name: 'Decathlon', src: '/brands/decathlon.svg' },
    { name: 'Betano', src: '/brands/betano.svg' },
    { name: 'Placard.pt', src: '/brands/placard.png' },
    { name: 'Brisa', src: '/brands/brisa.png' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AuthSplitLayout({
    children,
    footer,
}: {
    children: React.ReactNode;
    footer?: React.ReactNode;
}) {
    const { t, i18n } = useTranslation();
    const { mode, setMode } = useColorScheme();
    const prefersReducedMotion = usePrefersReducedMotion();
    const isLateNight = useLateNightEasterEgg();

    // Public auth pages resolve the language like /onboarding: explicit `?language=` (homepage link)
    // first, then the `language` cookie from a previous visit, then the browser language.
    useEffect(() => {
        const fromQuery = new URLSearchParams(window.location.search).get('language');
        i18n.changeLanguage(fromQuery || Cookies.get(LANGUAGE_COOKIE) || navigator.language);
    }, [i18n]);

    // Testimonial rotation
    const [testimonialIndex, setTestimonialIndex] = useState(0);
    const [testimonialVisible, setTestimonialVisible] = useState(true);

    useEffect(() => {
        if (prefersReducedMotion) return;
        const interval = setInterval(() => {
            setTestimonialVisible(false);
            setTimeout(() => {
                setTestimonialIndex((i) => (i + 1) % AUTH_TESTIMONIALS.length);
                setTestimonialVisible(true);
            }, 400);
        }, 7000);
        return () => clearInterval(interval);
    }, [prefersReducedMotion]);

    const testimonial = AUTH_TESTIMONIALS[testimonialIndex];
    const isDark = mode === 'dark';

    return (
        <AuthShell>
            {/* ── Left Panel ────────────────────────────── */}
            <LeftPanel>
                {/* Logo */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: 24, md: 32 },
                        left: { xs: '50%', md: '50%' },
                        transform: 'translateX(-50%)',
                    }}
                >
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        color="primary.main"
                        sx={{ letterSpacing: '-0.5px' }}
                    >
                        swonkie
                    </Typography>
                </Box>

                {/* Dark mode toggle — top-right corner */}
                <Tooltip title={isDark ? t('auth.theme.switch-light') : t('auth.theme.switch-dark')}>
                    <IconButton
                        onClick={() => setMode(isDark ? 'light' : 'dark')}
                        size="small"
                        aria-label={isDark ? t('auth.theme.switch-light') : t('auth.theme.switch-dark')}
                        sx={{
                            position: 'absolute',
                            top: { xs: 16, md: 24 },
                            right: { xs: 16, md: 24 },
                            color: 'text.secondary',
                        }}
                    >
                        {isDark ? (
                            <LightModeRounded fontSize="small" />
                        ) : (
                            <DarkModeRounded fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>

                {/* Form Card */}
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 480,
                        bgcolor: 'background.paper',
                        borderRadius: 4,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        boxShadow: 4,
                        p: { xs: 3, sm: 4 },
                        mt: { xs: 2, md: 0 },
                    }}
                >
                    {children}
                </Box>

                {/* Footer — rendered outside the card, below it */}
                {footer && <Box sx={{ mt: 3, textAlign: 'center' }}>{footer}</Box>}

                {/* Late Night Easter Egg */}
                {isLateNight && (
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        textAlign="center"
                        sx={{
                            mt: 3,
                            opacity: 0,
                            animation: 'lateNightFade 0.8s ease 0.1s forwards',
                            '@keyframes lateNightFade': {
                                to: { opacity: 1 },
                            },
                        }}
                    >
                        {t('auth.easter-egg.late-night')}
                    </Typography>
                )}
            </LeftPanel>

            {/* ── Right Panel ───────────────────────────── */}
            <RightPanel>
                {/* Testimonial */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Fade in={testimonialVisible} timeout={prefersReducedMotion ? 0 : 400}>
                        <Box key={testimonialIndex}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontStyle: 'italic',
                                    fontWeight: 400,
                                    lineHeight: 1.7,
                                    mb: 3,
                                    // Clamp to 3 lines on mobile to reduce height
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: { xs: 3, md: 'unset' },
                                    WebkitBoxOrient: 'vertical',
                                    '&::before': { content: '"\\201C"', mr: 0.25 },
                                    '&::after': { content: '"\\201D"', ml: 0.25 },
                                }}
                            >
                                {t(testimonial.quoteKey)}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar
                                    src={testimonial.avatarImage}
                                    sx={{
                                        width: { xs: 36, md: 44 },
                                        height: { xs: 36, md: 44 },
                                        bgcolor: testimonial.avatarColor,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {testimonial.avatarInitials}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {testimonial.authorName}
                                    </Typography>
                                    <Typography variant="caption">{t(testimonial.roleKey)}</Typography>
                                </Box>
                                {testimonial.companyLogo && (
                                    <Box
                                        component="img"
                                        src={testimonial.companyLogo}
                                        alt=""
                                        aria-hidden="true"
                                        sx={{
                                            ml: 'auto',
                                            height: 28,
                                            maxWidth: 72,
                                            objectFit: 'contain',
                                            filter: isDark ? 'brightness(0)' : 'brightness(0) invert(1)',
                                            opacity: 0.7,
                                        }}
                                    />
                                )}
                            </Stack>
                        </Box>
                    </Fade>
                </Box>

                {/* Trusted By */}
                <Box
                    sx={{
                        width: '100%',
                        pt: { xs: 2, md: 4 },
                        mt: 'auto',
                        borderTop: '1px solid',
                        borderColor: 'primary.contrastText',
                    }}
                >
                    <Typography
                        variant="overline"
                        sx={{
                            display: 'block',
                            mb: { xs: 1.5, md: 2 },
                            letterSpacing: '0.08em',
                        }}
                    >
                        {t('auth.social-proof.trusted-by')}
                    </Typography>
                    <Stack
                        direction="row"
                        alignItems={'center'}
                        justifyContent={'space-around'}
                        flexWrap={'wrap'}
                        gap={{ xs: 1, md: 2 }}
                        sx={{ mb: { xs: 1, md: 3 } }}
                    >
                        {TRUSTED_BRANDS.map((brand) => (
                            // Fixed-size card — all tiles are identical width × height.
                            // `object-fit: contain` centres the image inside the card
                            // regardless of its source aspect ratio, so square-ish logos
                            // (Brisa 1.28:1, RTP 1.36:1) letterbox naturally without
                            // distortion or any changes to the PNG files.
                            <Box
                                key={brand.name}
                                sx={{
                                    width: { xs: 72, md: 96 },
                                    height: { xs: 36, md: 48 },
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'common.white',
                                    borderRadius: 2,
                                    padding: '6px',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={brand.src}
                                    alt={brand.name}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        display: 'block',
                                    }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </RightPanel>
        </AuthShell>
    );
}
